import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';
import { ReactSketchCanvas, ReactSketchCanvasRef } from 'react-sketch-canvas';

import { getDataObjectUrlFromSvgString } from '../../scenes/Form/UploadDocumentPage/canvasUtils';

import { CanvasPath } from 'react-sketch-canvas/src/types';

interface Props {
  brushSize?: number;
  brushColor?: string;
  backgroundImage?: string;
  className?: string;
  /** Only applies if no background image used */
  canvasColor?: string;
  defaultPaths?: CanvasPath[];
  onChange?: (path: CanvasPath[]) => void;
}

export interface CanvasHandle {
  exportImage: (exportWidth: number, exportHeight: number) => Promise<string>;
  exportPaths: () => Promise<CanvasPath[]>;
  clear: () => void;
}

export const Canvas = React.forwardRef(
  (
    {
      brushSize = 3,
      brushColor = 'black',
      className,
      backgroundImage = '',
      canvasColor = 'transparent',
      defaultPaths: _defaultPaths,
      onChange,
    }: Props,
    forwardRef: React.Ref<CanvasHandle>,
  ) => {
    const [defaultPaths] = useState(_defaultPaths);
    const canvasRef = useRef<ReactSketchCanvasRef>(null);
    useImperativeHandle(
      forwardRef,
      () => ({
        exportPaths: async () => {
          if (!canvasRef.current)
            throw new Error('Canvas ref is not ready yet.');
          return canvasRef.current?.exportPaths();
        },
        exportImage: async (exportWidth: number, exportHeight: number) => {
          if (!canvasRef.current)
            throw new Error('Canvas ref is not ready yet.');
          const exportedSvgString = await canvasRef.current.exportSvg();
          const strokeBlobUrl =
            getDataObjectUrlFromSvgString(exportedSvgString);
          const exportImageDataUrl = await generateImageDataUrl({
            backgroundImageSource: backgroundImage,
            strokeSource: strokeBlobUrl,
            exportWidth,
            exportHeight,
          });
          URL.revokeObjectURL(strokeBlobUrl);
          return exportImageDataUrl;
        },
        clear: () => {
          canvasRef.current?.clearCanvas();
        },
      }),
      [backgroundImage],
    );
    useEffect(() => {
      if (defaultPaths) {
        canvasRef.current?.loadPaths(defaultPaths);
      }
    }, [defaultPaths]);
    return (
      <ReactSketchCanvas
        ref={canvasRef}
        strokeWidth={brushSize}
        strokeColor={brushColor}
        style={{}}
        backgroundImage={backgroundImage}
        exportWithBackgroundImage={true}
        canvasColor={canvasColor}
        preserveBackgroundImageAspectRatio="xMidYMid"
        onChange={onChange}
        className={className}
      />
    );
  },
);

const generateImageDataUrl = async ({
  strokeSource,
  exportWidth,
  exportHeight,
  backgroundImageSource,
}: {
  strokeSource: string;
  exportWidth: number;
  exportHeight: number;
  backgroundImageSource?: string;
}): Promise<string> =>
  new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d') as CanvasRenderingContext2D;
    canvas.width = exportWidth;
    canvas.height = exportHeight;
    const setUpStrokeImage = () => {
      const stokeImage = new Image();
      stokeImage.src = strokeSource;
      stokeImage.onload = function () {
        ctx.drawImage(stokeImage, 0, 0, exportWidth, exportHeight);
        resolve(canvas.toDataURL('image/png'));
      };
    };
    if (!backgroundImageSource) return setUpStrokeImage();
    const backgroundImageElement = new Image();
    backgroundImageElement.src = backgroundImageSource;
    backgroundImageElement.onload = () => {
      const heightRatio = exportHeight / backgroundImageElement.height;
      const widthRatio = exportWidth / backgroundImageElement.width;
      const scalingRatio = Math.min(heightRatio, widthRatio);
      const scaledX = backgroundImageElement.width * scalingRatio;
      const scaledY = backgroundImageElement.height * scalingRatio;
      const shiftX = (canvas.width - scaledX) / 2;
      const shiftY = (canvas.height - scaledY) / 2;
      ctx.drawImage(backgroundImageElement, shiftX, shiftY, scaledX, scaledY);
      setUpStrokeImage();
    };
  });
